import { SignedIn, SignedOut, SignIn, useUser } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useRole } from './hooks/useRole';
import ClientsList from './screens/ClientsList';
import ClientWorkspace from './screens/ClientWorkspace';
import DiscoveryBrief from './screens/DiscoveryBrief';
import ICP from './screens/ICP';
import FindSimilar from './screens/FindSimilar';
import FindSimilarContacts from './screens/FindSimilarContacts';
import Prospects from './screens/Prospects';
import Sequences from './screens/Sequences';
import LinkedinBatches from './screens/LinkedinBatches';
import EmailBatches from './screens/EmailBatches';
import Settings from './screens/Settings';
import Pricing from './screens/Pricing';
import ActivityLog from './screens/ActivityLog';
import PromptLibrary from './screens/PromptLibrary';
import Intelligence from './screens/Intelligence';
import Usage from './screens/Usage';
import Account from './screens/Account';
import WorkspaceUsers from './screens/WorkspaceUsers';
import AccountsList from './screens/AccountsList';
import AccountDetail from './screens/AccountDetail';
import Dashboard from './screens/Dashboard';
import ClientDashboard from './screens/ClientDashboard';
import PendingApproval from './screens/PendingApproval';
import UserApproval from './screens/UserApproval';
import AdminCosts from './screens/AdminCosts';
import AdminSocial from './screens/AdminSocial';
import Register from './screens/Register';
import { Layout } from './components/Layout';
import Documents from './screens/Documents';
import ClientDocuments from './screens/ClientDocuments';
import Webhooks from './screens/Webhooks';
import MailLayout from './components/MailLayout';
import CrmShell from './components/CrmShell';
import CrmContacts from './screens/crm/CrmContacts';
import CrmContactRecord from './screens/crm/CrmContactRecord';
import CrmCompanies from './screens/crm/CrmCompanies';
import CrmCompanyRecord from './screens/crm/CrmCompanyRecord';
import CrmFindSimilar from './screens/crm/CrmFindSimilar';
import CrmIntelligence from './screens/crm/CrmIntelligence';
import CrmMarket from './screens/crm/CrmMarket';
import CrmHome from './screens/crm/CrmHome';
import CrmClients from './screens/crm/CrmClients';
import CrmAttribution from './screens/crm/CrmAttribution';
import CrmPerformance from './screens/crm/CrmPerformance';
import CrmClientProfile from './screens/crm/CrmClientProfile';
import CrmEmailBatches from './screens/crm/CrmEmailBatches';
import CrmLinkedinBatches from './screens/crm/CrmLinkedinBatches';
import CrmPlaceholder from './screens/crm/CrmPlaceholder';
import CrmLists from './screens/crm/CrmLists';
import CrmSequences2 from './screens/crm/CrmSequences2';
import CrmOpportunities from './screens/crm/CrmOpportunities';
import CrmOpportunityRecord from './screens/crm/CrmOpportunityRecord';
import CrmSequences from './screens/crm/CrmSequences';
import CrmCampaigns from './screens/crm/CrmCampaigns';
import CrmInbox from './screens/crm/CrmInbox';
import CrmDeliverability from './screens/crm/CrmDeliverability';
import CrmMailboxDetail from './screens/crm/CrmMailboxDetail';
import MailInbox from './screens/MailInbox';
import MailOutbox from './screens/MailOutbox';
import MailSent from './screens/MailSent';
import Mailboxes from './screens/Mailboxes';
import MailboxDetail from './screens/MailboxDetail';
import MailboxPools from './screens/MailboxPools';
import Personas from './screens/Personas';
import Campaigns from './screens/Campaigns';
import CampaignDetail from './screens/CampaignDetail';
import WarmupDashboard from './screens/WarmupDashboard';
import EmailMetrics from './screens/EmailMetrics';
import Suppressions from './screens/Suppressions';
import Snippets from './screens/Snippets';
import Signatures from './screens/Signatures';
import AdminWebhooks from './screens/AdminWebhooks';
import WebhooksDocs from './screens/WebhooksDocs';
import { Toaster } from "react-hot-toast";
import { useState, useEffect } from 'react';
import API_BASE from './config';
import OutOfCreditsModal from './components/OutOfCreditsModal';
import LimitReachedModal from './components/LimitReachedModal';
import { CLIENT_PORTAL } from './features';

function RoleGuard({ children }) {
  const { user, isLoaded } = useUser();
  if (!isLoaded) return null;
  const role = user?.publicMetadata?.role;
  const knownRoles = ['super_admin', 'team_member', 'client_admin', 'client_member'];
  if (isLoaded && user && !knownRoles.includes(role)) {
    return <Navigate to="/pending" replace />;
  }
  return children;
}

function HomeRoute() {
  const { isClient } = useRole();
  if (isClient) return <Navigate to="/workspace" replace />;
  return <Navigate to="/crm/clients" replace />;
}

function AdminRoute({ children }) {
  const { isSuperAdmin } = useRole();
  return isSuperAdmin ? children : <Navigate to="/" replace />;
}

function AdminOrTeamRoute({ children }) {
  const { isSuperAdmin, isTeamMember } = useRole();
  return (isSuperAdmin || isTeamMember) ? children : <Navigate to="/" replace />;
}

function ClientAdminRoute({ children }) {
  const { isSuperAdmin, isTeamMember, isClientAdmin } = useRole();
  return (isSuperAdmin || isTeamMember || isClientAdmin) ? children : <Navigate to="/" replace />;
}

function App() {
  const [creditError, setCreditError] = useState(null);
  const [limitError, setLimitError] = useState(null);
  useEffect(() => {
    function onCreditError(e) { setCreditError(e.detail || { error: 'insufficient_credits' }); }
    function onLimitError(e) { setLimitError(e.detail || { error: 'limit_reached' }); }
    window.addEventListener('ce:creditError', onCreditError);
    window.addEventListener('ce:limitError', onLimitError);
    return () => {
      window.removeEventListener('ce:creditError', onCreditError);
      window.removeEventListener('ce:limitError', onLimitError);
    };
  }, []);

  return (
    <BrowserRouter>
      <Toaster
        containerStyle={{ zIndex: 99999 }}
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: {
            background: "#fff",
            color: "#12140F",
            border: "1px solid #e5e5e5",
            borderLeft: "4px solid #8B1A1A",
            padding: "12px 16px",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
          },
          success: { iconTheme: { primary: "#8B1A1A", secondary: "#fff" } },
          error: { iconTheme: { primary: "#8B1A1A", secondary: "#fff" } }
        }}
      />
      <Routes>
        {/* Public routes — no auth required */}
        <Route path="/register" element={<Register />} />
        <Route path="/pending" element={<PendingApproval />} />

        {/* Operator CRM shell — id-less, global screens only */}
        <Route path="/crm" element={
          <SignedIn>
            <RoleGuard>
              <CrmShell />
            </RoleGuard>
          </SignedIn>
        }>
          <Route index element={<Navigate to="clients" replace />} />
          <Route path="clients" element={<CrmClients />} />
          <Route path="clients/:clientId/profile" element={<CrmClientProfile />} />
          <Route path="home" element={<CrmHome />} />
          <Route path="attribution" element={<CrmAttribution />} />
        </Route>

        {/* CRM — full-screen shell, auth-protected, outside classic Layout */}
        <Route path="/clients/:id/crm" element={
          <SignedIn>
            <RoleGuard>
              <CrmShell />
            </RoleGuard>
          </SignedIn>
        }>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<CrmHome />} />
          <Route path="clients" element={<CrmClients />} />
          <Route path="attribution" element={<CrmAttribution />} />
          <Route path="contacts" element={<CrmContacts />} />
          <Route path="contacts/:contactId" element={<CrmContactRecord />} />
          <Route path="companies" element={<CrmCompanies />} />
          <Route path="companies/:companyId" element={<CrmCompanyRecord />} />
          <Route path="find-similar/:entity/:seedId" element={<CrmFindSimilar />} />
          <Route path="opportunities" element={<CrmOpportunities />} />
          <Route path="opportunities/:oppId" element={<CrmOpportunityRecord />} />
          <Route path="lists" element={<CrmLists />} />
          <Route path="inbox" element={<CrmInbox />} />
          <Route path="deliverability" element={<CrmDeliverability />} />
          <Route path="deliverability/mailboxes/:mailboxId" element={<CrmMailboxDetail />} />
          <Route path="sequences" element={<CrmSequences2 />} />
          <Route path="campaigns" element={<CrmCampaigns />} />
          <Route path="campaigns/:campaignId" element={<CrmCampaigns />} />
          <Route path="email-batches" element={<CrmEmailBatches />} />
          <Route path="linkedin-batches" element={<CrmLinkedinBatches />} />
          <Route path="intelligence" element={<CrmIntelligence />} />
          <Route path="market" element={<CrmMarket />} />
          <Route path="signals" element={<Navigate to="../intelligence" replace />} />
          <Route path="performance" element={<CrmPerformance />} />
          <Route path="metrics" element={<Navigate to="../performance" replace />} />
          <Route path="profile" element={<CrmClientProfile />} />
          <Route path="discovery" element={<Navigate to="../profile?s=discovery" replace />} />
          <Route path="icp" element={<Navigate to="../profile?s=icp" replace />} />
          <Route path="account" element={CLIENT_PORTAL ? <CrmPlaceholder /> : <Navigate to="../profile" replace />} />
          <Route path="usage" element={CLIENT_PORTAL ? <CrmPlaceholder /> : <Navigate to="../performance" replace />} />
          <Route path="documents" element={CLIENT_PORTAL ? <CrmPlaceholder /> : <Navigate to="../home" replace />} />
          <Route path="webhooks" element={CLIENT_PORTAL ? <CrmPlaceholder /> : <Navigate to="../home" replace />} />
        </Route>

        {/* All other routes */}
        <Route path="*" element={
          <>
            <SignedOut>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#12140F' }}>
                <SignIn />
              </div>
            </SignedOut>
            <SignedIn>
              <RoleGuard>
                <Layout>
                  <Routes>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/workspace" element={<ClientDashboard />} />
                    <Route path="/workspace/users" element={<WorkspaceUsers />} />
                    <Route path="/clients" element={<ClientsList />} />
                    <Route path="/clients/:id" element={<ClientWorkspace />} />
                    <Route path="/clients/:id/discovery" element={<DiscoveryBrief />} />
                    <Route path="/clients/:id/icp" element={<ICP />} />
                    <Route path="/clients/:id/prospects" element={<Prospects />} />
                    <Route path="/clients/:id/find-similar" element={<FindSimilar />} />
                    <Route path="/clients/:id/find-similar-contacts" element={<FindSimilarContacts />} />
                    <Route path="/clients/:id/sequences" element={<Sequences />} />
                    <Route path="/clients/:id/linkedin-batches" element={<LinkedinBatches />} />
                    <Route path="/clients/:id/email-batches" element={<EmailBatches />} />
                    <Route path="/clients/:id/activity" element={<ActivityLog />} />
                    <Route path="/clients/:id/intelligence" element={<Intelligence />} />
                    <Route path="/clients/:id/usage" element={<Usage />} />
                    <Route path="/clients/:id/account" element={<Account />} />
                    <Route path="/clients/:id/webhooks" element={<Webhooks />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/prompts" element={<PromptLibrary />} />
                    <Route path="/admin/users" element={<AdminRoute><UserApproval /></AdminRoute>} />
                    <Route path="/admin/accounts" element={<AdminRoute><AccountsList /></AdminRoute>} />
                    <Route path="/admin/accounts/:id" element={<AdminRoute><AccountDetail /></AdminRoute>} />
                    <Route path="/admin/costs" element={<AdminRoute><AdminCosts /></AdminRoute>} />
                    <Route path="/admin/social" element={<AdminRoute><AdminSocial /></AdminRoute>} />
                    <Route path="/admin/webhooks" element={<AdminRoute><AdminWebhooks /></AdminRoute>} />
                    <Route path="/documents" element={<AdminOrTeamRoute><Documents /></AdminOrTeamRoute>} />
                    <Route path="/my-documents" element={<ClientAdminRoute><ClientDocuments /></ClientAdminRoute>} />
                    <Route path="/docs/webhooks" element={<WebhooksDocs />} />
                    <Route path="/clients/:id/mail" element={<MailLayout />}>
                      <Route index element={<Navigate to="inbox" replace />} />
                      <Route path="inbox" element={<MailInbox />} />
                      <Route path="outbox" element={<MailOutbox />} />
                      <Route path="sent" element={<MailSent />} />
                      <Route path="campaigns" element={<Campaigns />} />
                      <Route path="campaigns/:campaignId" element={<CampaignDetail />} />
                      <Route path="mailboxes" element={<Mailboxes />} />
                      <Route path="mailboxes/:mailboxId" element={<MailboxDetail />} />
                      <Route path="pools" element={<MailboxPools />} />
                      <Route path="personas" element={<Personas />} />
                      <Route path="warmup" element={<WarmupDashboard />} />
                      <Route path="metrics" element={<EmailMetrics />} />
                      <Route path="suppressions" element={<Suppressions />} />
                      <Route path="snippets" element={<Snippets />} />
                      <Route path="signatures" element={<Signatures />} />
                    </Route>
                    <Route path="/mail" element={<MailLayout />}>
                      <Route index element={<Navigate to="/mail/inbox" replace />} />
                      <Route path="inbox" element={<MailInbox />} />
                      <Route path="outbox" element={<MailOutbox />} />
                      <Route path="sent" element={<MailSent />} />
                      <Route path="campaigns" element={<Campaigns />} />
                      <Route path="campaigns/:id" element={<CampaignDetail />} />
                      <Route path="mailboxes" element={<Mailboxes />} />
                      <Route path="mailboxes/:mailboxId" element={<MailboxDetail />} />
                      <Route path="pools" element={<MailboxPools />} />
                      <Route path="warmup" element={<WarmupDashboard />} />
                      <Route path="metrics" element={<EmailMetrics />} />
                      <Route path="suppressions" element={<Suppressions />} />
                      <Route path="snippets" element={<Snippets />} />
                      <Route path="signatures" element={<Signatures />} />
                    </Route>
                  </Routes>
                </Layout>
              </RoleGuard>
            </SignedIn>
          </>
        } />
      </Routes>
          <OutOfCreditsModal
        open={!!creditError}
        error={creditError}
        onClose={() => setCreditError(null)}
        onTopUp={() => { setCreditError(null); }}
      />

      <LimitReachedModal
        open={!!limitError}
        error={limitError}
        onClose={() => setLimitError(null)}
        onUpgrade={() => { setLimitError(null); window.location.href = '/pricing'; }}
      />
    </BrowserRouter>
  );
}

export default App;
